import { useEffect, useId, useState } from 'react'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Checkbox } from '../../platform/primitives/Checkbox'
import { Field, fieldErrorId } from '../../platform/primitives/Field'
import { Money } from '../../platform/primitives/Money'
import { SearchField } from '../../platform/primitives/SearchField'
import { cleanTag } from '../../platform/primitives/TagEditor'
import { BULK_CAP } from '../transactions/selection'
import type { Txn } from '../transactions/transactionWire'
import { shortDate } from '../transactions/txnDisplay'
import { useTransactionPage } from '../transactions/useTransactions'
import { collidingTag, TagNameTakenError } from './tagRules'
import { canAddress, useCreateTag } from './useTagMutations'
import './tags.css'

const PAGE_SIZE = 20
const plural = (n: number) => `${n} transaction${n === 1 ? '' : 's'}`

/** The search box waits for a pause: every keystroke would otherwise be a request and a skeleton. */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return settled
}

/**
 * New tag. The backend has no standalone tags — a tag exists while a transaction carries it — so this
 * is name → pick transactions → add the tag to them.
 */
export function CreateTagDialog({ tags, onClose }: { tags: readonly string[]; onClose: () => void }) {
  const [name, setName] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // Snapshots, keyed by id, so a pick survives paging and a new search (the Transactions selection does the same).
  const [picked, setPicked] = useState<ReadonlyMap<string, Txn>>(new Map())
  const [taken, setTaken] = useState<{ value: string; existing: string } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const create = useCreateTag()
  const toast = useToast()
  const fieldId = useId()
  const pendingNoteId = useId()
  const settled = useDebounced(search.trim(), 250)
  const phone = useBreakpoint() === 'phone'

  const list = useTransactionPage({ page, page_size: PAGE_SIZE, ...(settled === '' ? {} : { search: settled }) })

  const cleaned = cleanTag(name)
  const existing = cleaned === '' ? undefined : (collidingTag(tags, '', cleaned) ?? (taken?.value === cleaned ? taken.existing : undefined))
  const collision =
    existing === undefined ? undefined : `A tag named "${existing}" already exists. Add it to transactions from the Transactions page.`
  const reason =
    cleaned === ''
      ? "Tag name can't be blank."
      : !canAddress(cleaned)
        ? '"." and ".." can\'t be used as tag names.'
        : picked.size === 0
          ? 'Select at least one transaction.'
          : null
  const blocked = reason !== null || collision !== undefined
  const describedBy = collision !== undefined ? fieldErrorId(fieldId) : reason !== null ? `${fieldId}-hint` : undefined
  const running = create.isPending
  const outcome = create.data

  function toggle(row: Txn) {
    setPicked((prev) => {
      const next = new Map(prev)
      if (next.has(row.id)) next.delete(row.id)
      else if (next.size < BULK_CAP) next.set(row.id, row)
      return next
    })
  }

  async function submit() {
    if (running || blocked) return
    setFailure(null)
    try {
      const result = await create.mutateAsync({ name: cleaned, rows: [...picked.values()] })
      if (result.failed.length === 0) {
        toast.show({ kind: 'success', message: `Added “${cleaned}” to ${plural(result.done.length)}` })
        onClose()
      }
    } catch (thrown) {
      if (thrown instanceof TagNameTakenError) {
        setTaken({ value: cleaned, existing: thrown.existing })
        return
      }
      setFailure("The tag wasn't created, and nothing was changed. Check your connection and try again.")
    }
  }

  return (
    <Overlay
      open
      onClose={() => {
        if (!running) onClose()
      }}
      surface="glass"
      placement="sheet"
      label="New tag"
    >
      {outcome !== undefined && outcome.failed.length > 0 ? (
        <div className="tags-dialog">
          <h3 className="tags-dialog__title">New tag</h3>
          <p role="status" className="tags-dialog__note">
            {outcome.done.length === 0
              ? `“${cleaned}” wasn't added. No transactions could be updated.`
              : `Added “${cleaned}” to ${plural(outcome.done.length)}; ${outcome.failed.length} failed.`}
          </p>
          <ul className="tags-create__failures">
            {outcome.failed.map((f) => {
              const row = picked.get(f.id)
              return (
                <li key={f.id}>
                  <strong>{row?.merchant ?? 'Transaction'}</strong>
                  {row && ` · ${shortDate(row.date)}`}
                  {` — ${f.reason}`}
                </li>
              )
            })}
          </ul>
          <div className="tags-dialog__actions">
            <Button type="button" variant="primary" size="form" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className={phone ? 'tags-dialog tags-dialog--fill' : 'tags-dialog'}
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <h3 className="tags-dialog__title">New tag</h3>
          <p className="tags-dialog__note">A tag exists while a transaction carries it. Name it, then choose the transactions to tag.</p>
          <Field
            id={fieldId}
            label="Tag name"
            size="form"
            value={name}
            autoComplete="off"
            error={collision}
            hint={collision === undefined ? (reason ?? undefined) : undefined}
            onChange={(e) => {
              setName(e.target.value)
              setFailure(null)
            }}
          />
          <SearchField
            className="tags-create__search"
            label="Search transactions"
            placeholder="Search merchant or description"
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
          />
          <Async
            surface="transactions"
            state={list}
            className="tags-create__list"
            skeleton={<Skeleton height={44} />}
            // Required by Async's type; unreachable, no `isEmpty` is passed: an empty search is the in-body note below.
            empty={{ title: 'No transactions' }}
          >
            {(data) => (
              <>
                {data.rows.length === 0 && <p className="tags-dialog__note">No transactions match.</p>}
                {data.rows.length > 0 && (
                  <ul className="tags-create__rows">
                    {data.rows.map((row) => {
                      const checked = picked.has(row.id)
                      const atCap = !checked && picked.size >= BULK_CAP
                      return (
                        <li key={row.id} className="tags-create__row">
                          <Checkbox
                            checked={checked}
                            disabled={row.isPending || atCap}
                            describedBy={row.isPending ? pendingNoteId : undefined}
                            label={`Select ${row.merchant}, ${shortDate(row.date)}`}
                            onChange={() => toggle(row)}
                          />
                          <span className="tags-create__what">
                            <span className="tags-create__merchant">{row.merchant}</span>
                            <span className="tags-create__date">{shortDate(row.date)}</span>
                            {row.isPending && <span className="tags-create__date">Pending</span>}
                          </span>
                          <Money cents={row.amountCents} currency={row.currency} kind="flow" />
                        </li>
                      )
                    })}
                  </ul>
                )}
                {data.rows.some((r) => r.isPending) && (
                  <p id={pendingNoteId} className="tags-dialog__note">
                    Pending transactions can&apos;t be tagged until the bank posts them.
                  </p>
                )}
                {(data.hasMore || page > 1) && (
                  <div className="tags__pager">
                    <Button type="button" variant="outline" size="panel" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      Prev
                    </Button>
                    <span className="tags__pager-label">{`Page ${page}`}</span>
                    <Button type="button" variant="outline" size="panel" disabled={!data.hasMore} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Async>
          <p className="tags-dialog__note" aria-live="polite">
            {picked.size >= BULK_CAP ? `${picked.size} selected (the most one tag can be added to at a time)` : `${picked.size} selected`}
          </p>
          {failure !== null && (
            <p className="tags-dialog__error" role="alert">
              {failure}
            </p>
          )}
          {running && create.progress !== null && (
            <p className="tags-dialog__note" role="status">{`Adding ${create.progress.done} of ${create.progress.total}…`}</p>
          )}
          <div className="tags-dialog__actions">
            <Button type="button" variant="chip" size="form" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="form"
              aria-busy={running}
              aria-disabled={blocked || undefined}
              aria-describedby={blocked ? describedBy : undefined}
            >
              {running ? 'Adding…' : `Add to ${plural(picked.size)}`}
            </Button>
          </div>
        </form>
      )}
    </Overlay>
  )
}
